import { useAccount } from "@apis/accounts/useAccounts"
import {
  useGetAllPlansQuery,
  useGetContractTypesQuery,
  useGetPlanSOPQuery,
} from "@apis/offers/useOffers"
import type { IPlan } from "@apis/offers/types"
import { useGetMarketRatesQuery } from "@apis/pricing/usePricing"

export const useSerializeCustomOfferForm = (account_id: string) => {
  const getAccountQuery = useAccount(account_id)
  const getMarketRatesQuery = useGetMarketRatesQuery(
    getAccountQuery.data?.market_code,
  )
  const getContractTypesQuery = useGetContractTypesQuery()
  const getAllPlansQuery = useGetAllPlansQuery()
  const getPlanSOPQuery = useGetPlanSOPQuery()

  const planDurations = getPlanDurations(
    getAllPlansQuery.data,
    getPlanSOPQuery.data,
  )

  const isPending =
    getMarketRatesQuery.isPending ||
    getContractTypesQuery.isPending ||
    getAllPlansQuery.isPending ||
    getPlanSOPQuery.isPending

  const isError =
    getMarketRatesQuery.isError ||
    getContractTypesQuery.isError ||
    getAllPlansQuery.isError ||
    getPlanSOPQuery.isError

  return {
    planSOP: getPlanSOPQuery.data,
    allPlans: getAllPlansQuery.data,
    contractTypes: getContractTypesQuery.data,
    marketRates: getMarketRatesQuery.data,
    account: getAccountQuery.data,
    planDurations,
    isPending,
    isError
  }
}

const getPlanDurations = (
  allPlans: IPlan[] | undefined,
  sopPlans: IPlan[] | undefined,
) => {
  if (!allPlans || !sopPlans) return undefined

  // sopPlans will only ever be 1–2, but this scales better if it grows
  const sopPlanCodes = new Set(sopPlans?.map((plan) => plan.plan_code))
  // filter allPlans against the Set
  const plans = allPlans?.filter((plan) => !sopPlanCodes.has(plan.plan_code))
  // get unique list of duration options
  const durations = new Set(plans?.map((plan) => plan.contract_duration))

  // sort by duration
  return Array.from(durations).sort(
    (a, b) => durationToMonths(a) - durationToMonths(b),
  )
}

// "P36M" to 36
export const durationToMonths = (str: string): number => {
  const match = str.match(/\d+/)
  return match ? Number(match[0]) : 0
}
